import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { ContactLookupInput } from "@/components/contact-lookup";
import { FieldShell } from "./crm-ui";
import { CrmFieldInput } from "./crm-field-input";
import { ContactPhoto } from "./crm-photo";
import { useCrmText } from "@/lib/crm/i18n";
import { fieldDef, guessEmailKind } from "@/lib/crm/fields";
import { crmFetch, crmKeys, setRecordCache, useCrmRecord, type CrmError, type CrmRecord } from "@/lib/crm/api";
import { apiErrorCode, changedValues, errorKey, toDraft, validateDraft, type Draft } from "@/lib/crm/form-values";
import { recordTitle } from "@/lib/crm/view-engine";
import { formatPhoneDisplay } from "@/lib/masks";
import { queryClient } from "@/lib/queryClient";
import { ArrowLeftRight, Loader2, Star, UserPlus } from "lucide-react";

interface Props {
  /** Linked contact id (the lead's client). */
  value: string | null;
  label: string;
  required?: boolean;
  error?: string;
  readOnly?: boolean;
  /** Lead data used to fill the "new client" form. */
  prefill?: Draft;
  testId?: string;
  onPick: (record: CrmRecord) => void;
}

/**
 * Client of a lead: search the registered clients by name (the most recent ones show on focus), or
 * register a new one right here. Picking shows the client's data on the lead.
 */
export function ClientLookupField({ value, label, required, error, readOnly, prefill, testId, onPick }: Props) {
  const { tx } = useCrmText();
  const { toast } = useToast();
  const client = useCrmRecord("contact", value);
  const [q, setQ] = useState("");
  const [changing, setChanging] = useState(false);
  const [creating, setCreating] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const pickId = async (id: string) => {
    setBusy(true);
    try {
      const r = await queryClient.fetchQuery({
        queryKey: crmKeys.record("contact", id),
        queryFn: () => crmFetch<CrmRecord>("GET", `/api/crm/contacts/${id}`),
        staleTime: 0,
      });
      setChanging(false);
      setQ("");
      onPick(r);
    } catch (e) {
      toast({ title: tx("error"), description: (e as Error).message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const dialog = (
    <NewClientDialog
      open={creating !== null}
      typed={creating || ""}
      prefill={prefill}
      onClose={() => setCreating(null)}
      onCreated={(r) => { setCreating(null); setChanging(false); setQ(""); onPick(r); }}
      onUseExisting={(id) => { setCreating(null); pickId(id); }}
    />
  );

  if (value && !changing) {
    const v = client.data?.values;
    const name = v ? recordTitle("contact", v) : "…";
    return (
      <FieldShell label={label} required={required} error={error}>
        <div className="flex min-h-9 flex-wrap items-center gap-2 rounded-md border bg-muted/30 px-2.5 py-1.5 text-sm" data-testid={testId ? `${testId}-linked` : "client-linked"}>
          <ContactPhoto contactId={value} version={v?.photoUpdatedAt} name={name} className="h-7 w-7 text-[11px]" />
          <div className="min-w-0 flex-1">
            <Link href={`/contacts/${value}`} className="block truncate font-medium underline-offset-2 hover:underline" data-testid="client-open">{name}</Link>
            <span className="block truncate text-xs text-muted-foreground">
              {[v?.email, v?.mobilePhone ? formatPhoneDisplay(v.mobilePhone) : "", v?.companyName].filter(Boolean).join(" · ")}
            </span>
          </div>
          {!readOnly && (
            <Button variant="ghost" size="sm" className="h-7 shrink-0" onClick={() => setChanging(true)} data-testid="client-change">
              <ArrowLeftRight className="h-3.5 w-3.5" /> {tx("changeClient")}
            </Button>
          )}
        </div>
        {dialog}
      </FieldShell>
    );
  }

  if (readOnly) {
    return (
      <FieldShell label={label} required={required} error={error}>
        <div className="flex min-h-9 items-center rounded-md border border-dashed bg-muted/40 px-3 text-sm text-muted-foreground" data-testid={testId}>—</div>
      </FieldShell>
    );
  }

  return (
    <FieldShell label={label} required={required} error={error} hint={tx("clientHint")}>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <ContactLookupInput
            mode="client"
            minChars={0}
            value={q}
            onChange={setQ}
            placeholder={tx("clientSearch")}
            aria-invalid={!!error}
            data-testid={testId}
            onPick={(c) => pickId(c.id)}
            onCreate={(typed) => setCreating(typed.trim())}
            createLabel={q.trim() ? tx("clientCreateNamed", { name: q.trim() }) : tx("clientCreate")}
          />
        </div>
        {busy && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />}
        {changing && (
          <Button variant="ghost" size="sm" className="h-8 shrink-0" onClick={() => { setChanging(false); setQ(""); }}>{tx("cancel")}</Button>
        )}
      </div>
      {dialog}
    </FieldShell>
  );
}

const NEW_CLIENT_FIELDS = ["firstName", "lastName", "personalEmail", "corporateEmail", "mobilePhone", "cpf", "companyName", "cnpj", "jobTitle"];

/** Quick registration of a client from the lead's client field. */
function NewClientDialog({ open, typed, prefill, onClose, onCreated, onUseExisting }: {
  open: boolean;
  typed: string;
  prefill?: Draft;
  onClose: () => void;
  onCreated: (r: CrmRecord) => void;
  onUseExisting: (id: string) => void;
}) {
  const { tx } = useCrmText();
  const { toast } = useToast();
  const [draft, setDraft] = useState<Draft>(() => toDraft("contact", {}));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [duplicate, setDuplicate] = useState<{ id: string; name: string } | null>(null);
  const [saving, setSaving] = useState(false);

  // Fresh form on every open: the typed name, else the lead's person data.
  useEffect(() => {
    if (!open) return;
    const d = toDraft("contact", {});
    d.preferredEmail = "personal";
    const name = typed.trim();
    if (name) {
      const parts = name.split(/\s+/);
      d.firstName = parts[0];
      d.lastName = parts.slice(1).join(" ");
    } else if (prefill) {
      d.firstName = prefill.firstName || "";
      d.lastName = prefill.lastName || "";
    }
    if (prefill) {
      const email = String(prefill.email || "").trim().toLowerCase();
      if (email) {
        const kind = guessEmailKind(email);
        d[kind === "personal" ? "personalEmail" : "corporateEmail"] = email;
        d.preferredEmail = kind;
      }
      d.mobilePhone = prefill.mobilePhone || "";
      d.businessPhone = prefill.businessPhone || "";
      d.companyName = prefill.companyName || "";
      d.jobTitle = prefill.jobTitle || "";
    }
    setDraft(d);
    setErrors({});
    setDuplicate(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (key: string, v: any) => {
    setDraft((d) => ({ ...d, [key]: v }));
    setErrors((e) => {
      const { [key]: _drop, ...rest } = e;
      if (key === "personalEmail" || key === "corporateEmail") { delete rest.personalEmail; delete rest.corporateEmail; }
      return rest;
    });
    if (key.endsWith("Email")) setDuplicate(null);
  };
  const message = (field: string, code: string) => tx(errorKey(apiErrorCode("contact", field, code)));

  const save = async () => {
    const found = validateDraft("contact", draft, undefined);
    if (Object.keys(found).length) {
      setErrors(Object.fromEntries(Object.entries(found).map(([k, c]) => [k, message(k, c)])));
      return;
    }
    setSaving(true);
    try {
      const r = await crmFetch<CrmRecord>("POST", "/api/crm/contacts", { values: changedValues("contact", null, draft) });
      setRecordCache(r);
      queryClient.invalidateQueries({ queryKey: crmKeys.list("contact") });
      toast({ title: tx("clientCreated") });
      onCreated(r);
    } catch (e) {
      const err = e as CrmError;
      if (err.code === "VALIDATION") {
        setErrors(Object.fromEntries(Object.entries(err.fieldErrors()).map(([k, c]) => [k, message(k, c)])));
      } else if (err.code === "DUPLICATE_EMAIL") {
        const d = { id: String(err.details?.existingId || ""), name: String(err.details?.existingName || "") };
        setDuplicate(d);
        setErrors({ [String(err.details?.field || "personalEmail")]: tx("duplicateEmail", { name: d.name }) });
      } else {
        toast({ title: tx("error"), description: err.message, variant: "destructive" });
      }
    } finally {
      setSaving(false);
    }
  };

  const both = !!String(draft.personalEmail || "").trim() && !!String(draft.corporateEmail || "").trim();
  const star = (kind: "personal" | "corporate") => {
    const key = kind === "personal" ? "personalEmail" : "corporateEmail";
    if (!both) return null;
    const isDefault = (draft.preferredEmail || "personal") === kind;
    return (
      <button type="button" onClick={() => set("preferredEmail", kind)} title={isDefault ? tx("defaultEmail") : tx("makeDefault")}
        className={`inline-flex items-center gap-0.5 text-[11px] ${isDefault ? "font-medium text-amber-600 dark:text-amber-400" : "text-muted-foreground hover:text-foreground"}`}
        data-testid={`new-client-default-${key}`}>
        <Star className={`h-3 w-3 ${isDefault ? "fill-current" : ""}`} /> {isDefault ? tx("defaultEmail") : tx("makeDefault")}
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="new-client-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5" /> {tx("newClientTitle")}</DialogTitle>
          <DialogDescription>{tx("newClientDesc")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {NEW_CLIENT_FIELDS.map((key) => {
            const def = fieldDef("contact", key)!;
            return (
              <CrmFieldInput key={key} entity="contact" def={def} value={draft[key]} onChange={(v) => set(key, v)} error={errors[key]}
                required={key === "firstName" || ((key === "personalEmail" || key === "corporateEmail") && !String(draft.personalEmail || "").trim() && !String(draft.corporateEmail || "").trim())}
                members={[]} labelAddon={key === "personalEmail" ? star("personal") : key === "corporateEmail" ? star("corporate") : undefined} />
            );
          })}
        </div>
        {duplicate?.id && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            <span className="min-w-0 flex-1">{tx("duplicateEmail", { name: duplicate.name })}</span>
            <Button size="sm" variant="outline" onClick={() => onUseExisting(duplicate.id)} data-testid="new-client-use-existing">
              {tx("useExistingClient", { name: duplicate.name })}
            </Button>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{tx("cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="new-client-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {tx("createAndLink")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
